import {survey_config_base} from 'screens/InsomniaSurvey/utils/__survey_config_base';
import {interstitial} from 'screens/InsomniaSurvey/utils/interstitial';
import {radioPage} from 'screens/InsomniaSurvey/utils/page';
import {RiseSurveyConfig} from 'types';
import 'surveys/osa_funnel/osaTheme.css';
import {AngerScience} from '../components/ScienceScreen/AngerScience';
import {BargainingScience} from '../components/ScienceScreen/BargainingScience';
import {RealizationScience} from '../components/ScienceScreen/RealizationScience';
import {AcceptanceScience} from '../components/ScienceScreen/AcceptanceScience';
import {DismissSurvey} from '../components/DismissSurvey/DismissSurvey';
import {Glp1EducationProtocol} from '../components/education/Glp1EducationProtocol';
import {Glp1EducationIntro} from '../components/education/Glp1EducationIntro';
import {Glp1EducationFamily} from '../components/education/Glp1EducationFamily';
import {Glp1EducationOutcome} from '../components/education/Glp1EducationOutcome';
import {Glp1EducationLegitimacy} from '../components/education/Glp1EducationLegitimacy';
import {Glp1PrimerSlideA} from '../components/primer/Glp1PrimerSlideA';
import {Glp1PrimerSlideB} from '../components/primer/Glp1PrimerSlideB';
import {Glp1PrimerSlideC} from '../components/primer/Glp1PrimerSlideC';
import {Glp1PrimerFilter} from '../components/primer/Glp1PrimerFilter';

const SURVEY_TITLE = 'osa_funnel';
const CATEGORY = 'Sleep apnea risk';

const NON_DISMISSED = '{osa_dismiss} <> true';
const DISMISSED = '{osa_dismiss} = true';

export const survey_config: RiseSurveyConfig = {
  ...survey_config_base,
  title: SURVEY_TITLE,
  enableAnswerNewStyle: true,
  socialProofFooter: [],
  showCategoryOnFirstPage: true,
  // Preserve the query string (returnTo, modality, severity) across the
  // survey → checkout hop so the eventual exit redirect honours ?returnTo=.
  onCompleteRedirectTo: () => `/osa/checkout${window.location.search}`,

  // Filter-exit branching: when the mask filter picks "probably_not" or "no",
  // osa_dismiss flips to true, the rest of the arc skips via visibleIf, and
  // the dismiss survey shows. The exhaustion filter lives in a different
  // funnel now.
  calculatedValues: [
    {
      name: 'osa_dismiss',
      expression: "{grief_mask_interest} = 'probably_not' or {grief_mask_interest} = 'no' or {primer_filter} = 'no'",
    },
    {
      name: 'grief_exit_reason',
      // Tracks WHICH filter dismissed the user. Mask-interest filter
      // resolves first (it sits earlier in the flow); the primer filter
      // only fires after the education arc, so when both could match we
      // prefer the more specific (later) reason.
      expression:
        "iif({primer_filter} = 'no', 'primer_filter', iif({grief_mask_interest} = 'probably_not' or {grief_mask_interest} = 'no', 'mask_interest', ''))",
    },
    {
      name: 'grief_exit_value',
      expression:
        "iif({primer_filter} = 'no', {primer_filter}, iif({grief_mask_interest} = 'probably_not' or {grief_mask_interest} = 'no', {grief_mask_interest}, ''))",
    },
  ],

  pages: [
    radioPage({
      category: CATEGORY,
      name: 'grief_anger',
      title: 'Have you been trying to fix your sleep on your own and getting nowhere?',
      description: "What you've already tried matters here.",
      choices: [
        {value: 'agree', text: 'Agree'},
        {value: 'disagree', text: 'Disagree'},
      ],
      autoAdvance: true,
      isRequired: true,
    }),

    interstitial({
      name: 'osa_anger_science',
      component: AngerScience,
      category: CATEGORY,
    }),

    radioPage({
      category: CATEGORY,
      name: 'grief_bargaining',
      title: 'If you got stricter with your bedtime, do you think the exhaustion would go away?',
      description: "It's a fair thing to wonder. Most people in your situation do.",
      choices: [
        {value: 'agree', text: 'Agree'},
        {value: 'disagree', text: 'Disagree'},
      ],
      autoAdvance: true,
      isRequired: true,
    }),

    interstitial({
      name: 'osa_bargaining_science',
      component: BargainingScience,
      category: CATEGORY,
    }),

    radioPage({
      category: CATEGORY,
      name: 'grief_realization',
      title: 'Have you ever blamed yourself for being tired all the time?',
      description: 'A lot of people in your situation have.',
      choices: [
        {value: 'agree', text: 'Agree'},
        {value: 'disagree', text: 'Disagree'},
      ],
      autoAdvance: true,
      isRequired: true,
    }),

    interstitial({
      name: 'osa_realization_science',
      component: RealizationScience,
      category: CATEGORY,
    }),

    radioPage({
      category: CATEGORY,
      name: 'grief_mask_interest',
      title: 'If a doctor had a magic pill to fix sleep apnea — no mask, no machine — would you take it?',
      description: 'This decides where we go from here.',
      choices: [
        {value: 'yes', text: "Yes, I'm done trying to fix this alone"},
        {value: 'maybe', text: "Maybe, depends on what's involved"},
        {value: 'probably_not', text: "Not yet, I'd rather try other approaches first"},
        {value: 'no', text: "No, medication isn't an option I'd consider"},
      ],
      autoAdvance: true,
      isRequired: true,
    }),

    interstitial({
      name: 'osa_acceptance_science',
      component: AcceptanceScience,
      category: CATEGORY,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_education_protocol',
      component: Glp1EducationProtocol,
      category: CATEGORY,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_education_intro',
      component: Glp1EducationIntro,
      category: CATEGORY,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_education_family',
      component: Glp1EducationFamily,
      category: CATEGORY,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_education_outcome',
      component: Glp1EducationOutcome,
      category: CATEGORY,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_education_legitimacy',
      component: Glp1EducationLegitimacy,
      category: CATEGORY,
      autoAdvance: true,
      visibleIf: NON_DISMISSED,
    }),

    // Glp1 Primer — 4 separate survey pages so the main progress bar
    // advances per slide and browser back navigates between them. The
    // final Filter slide writes `primer_filter` to survey data, which
    // feeds osa_dismiss: a "no" pick routes the user to the dismiss
    // survey; "yes" / "maybe" complete the survey and redirect to
    // /osa/checkout (paywall).
    interstitial({
      name: 'osa_primer_a',
      component: Glp1PrimerSlideA,
      category: CATEGORY,
      autoAdvance: true,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_primer_b',
      component: Glp1PrimerSlideB,
      category: CATEGORY,
      autoAdvance: true,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_primer_c',
      component: Glp1PrimerSlideC,
      category: CATEGORY,
      autoAdvance: true,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_primer_filter',
      component: Glp1PrimerFilter,
      category: CATEGORY,
      autoAdvance: true,
      visibleIf: NON_DISMISSED,
    }),

    interstitial({
      name: 'osa_dismiss_survey',
      component: DismissSurvey,
      category: CATEGORY,
      autoAdvance: true,
      visibleIf: DISMISSED,
    }),
  ],
};

export default survey_config;
